/**
 * FL-246: スケジュール承認パネル・コンポーネント
 *
 * 管理者が登録状態のスケジュールを確認・承認・キャンセルするUI
 * 個別操作と一括操作の両方に対応
 *
 * TDD Red-Green-Refactorサイクルに従った実装
 */

import React, { useState } from 'react';
import { DriverSchedule } from '@/types/schedule';

interface ScheduleApprovalPanelProps {
  /** 承認対象のスケジュール（登録状態のみ） */
  schedules: DriverSchedule[];
  /** スケジュール承認時のコールバック */
  onApprove: (scheduleId: string, comment?: string) => void;
  /** スケジュールキャンセル時のコールバック */
  onCancel: (scheduleId: string, reason: string) => void;
  /** 一括承認時のコールバック */
  onBatchApprove: (scheduleIds: string[]) => void;
  /** 一括キャンセル時のコールバック */
  onBatchCancel: (scheduleIds: string[], reason: string) => void;
  /** ドライバー情報（ID → 名前のマッピング） */
  driverNameMap?: Record<string, string>;
}

/**
 * ScheduleApprovalPanel コンポーネント
 *
 * @param schedules - 承認対象スケジュール
 * @param onApprove - 承認コールバック
 * @param onCancel - キャンセルコールバック
 * @param onBatchApprove - 一括承認コールバック
 * @param onBatchCancel - 一括キャンセルコールバック
 * @param driverNameMap - ドライバー名マッピング
 * @returns JSX要素
 */
export const ScheduleApprovalPanel: React.FC<ScheduleApprovalPanelProps> = ({
  schedules,
  onApprove,
  onCancel,
  onBatchApprove,
  onBatchCancel,
  driverNameMap = {},
}) => {
  // 複数選択管理
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<Set<string>>(new Set());

  // コメント・理由の管理
  const [comments, setComments] = useState<Record<string, string>>({});
  const [batchReason, setBatchReason] = useState<string>('');
  const [showBatchReasonInput, setShowBatchReasonInput] = useState<boolean>(false);

  /**
   * スケジュール選択トグル
   */
  const handleToggleSelection = (scheduleId: string) => {
    const newSelected = new Set(selectedScheduleIds);
    if (newSelected.has(scheduleId)) {
      newSelected.delete(scheduleId);
    } else {
      newSelected.add(scheduleId);
    }
    setSelectedScheduleIds(newSelected);
  };

  /**
   * 全て選択
   */
  const handleSelectAll = () => {
    setSelectedScheduleIds(new Set(schedules.map(s => s.id)));
  };

  /**
   * 選択解除
   */
  const handleDeselectAll = () => {
    setSelectedScheduleIds(new Set());
  };

  /**
   * 個別承認
   */
  const handleIndividualApprove = (scheduleId: string) => {
    const comment = comments[scheduleId] || '';
    onApprove(scheduleId, comment);
    // UI更新（該当スケジュールの削除は親で処理）
  };

  /**
   * 個別キャンセル
   */
  const handleIndividualCancel = (scheduleId: string) => {
    const reason = comments[scheduleId] || '';
    if (!reason) {
      alert('キャンセル理由を入力してください');
      return;
    }
    onCancel(scheduleId, reason);
  };

  /**
   * 一括承認
   */
  const handleBatchApprove = () => {
    if (selectedScheduleIds.size === 0) return;
    onBatchApprove(Array.from(selectedScheduleIds));
    setSelectedScheduleIds(new Set());
  };

  /**
   * 一括キャンセル
   */
  const handleBatchCancel = () => {
    if (selectedScheduleIds.size === 0) return;
    if (!batchReason.trim()) {
      alert('キャンセル理由を入力してください');
      return;
    }
    onBatchCancel(Array.from(selectedScheduleIds), batchReason);
    setSelectedScheduleIds(new Set());
    setBatchReason('');
    setShowBatchReasonInput(false);
  };

  return (
    <div
      data-testid='schedule-approval-panel'
      className='space-y-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'
    >
      {/* タイトル */}
      <div>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>スケジュール承認</h3>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          {schedules.length}件の承認待ちスケジュール
        </p>
      </div>

      {/* スケジュール一覧が空の場合 */}
      {schedules.length === 0 ? (
        <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
          <p>承認待ちのスケジュールはありません</p>
        </div>
      ) : (
        <>
          {/* 一括操作ツールバー */}
          <div className='bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-3'>
            {/* 選択・一括操作ボタン */}
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                onClick={handleSelectAll}
                className='text-xs px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                data-testid='select-all-button'
              >
                全て選択（{schedules.length}）
              </button>
              <button
                type='button'
                onClick={handleDeselectAll}
                className='text-xs px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                data-testid='deselect-all-button'
              >
                選択解除
              </button>

              {/* 一括操作ボタン */}
              <button
                type='button'
                onClick={handleBatchApprove}
                disabled={selectedScheduleIds.size === 0}
                className='text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                data-testid='batch-approve-button'
              >
                全て承認（{selectedScheduleIds.size}）
              </button>

              <button
                type='button'
                onClick={() => setShowBatchReasonInput(!showBatchReasonInput)}
                disabled={selectedScheduleIds.size === 0}
                className='text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                data-testid='batch-cancel-button'
              >
                全てキャンセル（{selectedScheduleIds.size}）
              </button>
            </div>

            {/* 一括キャンセル理由入力 */}
            {showBatchReasonInput && (
              <div className='space-y-2'>
                <textarea
                  value={batchReason}
                  onChange={e => setBatchReason(e.target.value)}
                  placeholder='キャンセル理由を入力してください（例：悪天候）'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm'
                  rows={2}
                  data-testid='batch-cancel-reason'
                />
                <button
                  type='button'
                  onClick={handleBatchCancel}
                  disabled={!batchReason.trim()}
                  className='text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                  data-testid='batch-cancel-confirm'
                >
                  キャンセル実行
                </button>
              </div>
            )}
          </div>

          {/* スケジュール一覧 */}
          <div className='space-y-2 max-h-96 overflow-y-auto'>
            {schedules.map(schedule => {
              const driverName =
                driverNameMap[schedule.landTransporterId] || schedule.landTransporterId;
              const isSelected = selectedScheduleIds.has(schedule.id);

              return (
                <div
                  key={schedule.id}
                  className={`border rounded-lg p-3 space-y-2 ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                  }`}
                  data-testid={`schedule-item-${schedule.id}`}
                >
                  {/* スケジュール情報 */}
                  <div className='flex items-start gap-3'>
                    {/* チェックボックス */}
                    <input
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => handleToggleSelection(schedule.id)}
                      className='h-4 w-4 rounded border-gray-300 text-blue-600 mt-1'
                      data-testid={`schedule-checkbox-${schedule.id}`}
                    />

                    {/* 情報 */}
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {driverName}
                      </p>
                      <p className='text-xs text-gray-600 dark:text-gray-400'>
                        {schedule.scheduleDate} {schedule.timeStart} - {schedule.timeEnd}
                      </p>
                      {schedule.notes && (
                        <p className='text-xs text-gray-500 dark:text-gray-500 mt-1'>
                          {schedule.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* コメント・理由入力 */}
                  <textarea
                    value={comments[schedule.id] || ''}
                    onChange={e =>
                      setComments(prev => ({
                        ...prev,
                        [schedule.id]: e.target.value,
                      }))
                    }
                    placeholder='コメント・キャンセル理由（キャンセル時は必須）'
                    className='w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs'
                    rows={2}
                    data-testid={`schedule-comment-${schedule.id}`}
                  />

                  {/* 個別操作ボタン */}
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      onClick={() => handleIndividualApprove(schedule.id)}
                      className='flex-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700'
                      data-testid={`approve-button-${schedule.id}`}
                    >
                      承認
                    </button>
                    <button
                      type='button'
                      onClick={() => handleIndividualCancel(schedule.id)}
                      className='flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700'
                      data-testid={`cancel-button-${schedule.id}`}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleApprovalPanel;
